import React, { useEffect, useState } from 'react';
import { ButtonColor, StyledButton } from '../../../common/Styleguide/Common/Button';
import ArrowLeft from '../../../common/icons/ArrowLeft';
import ArrowRight from '../../../common/icons/ArrowRight';
import { ActionLinkContainer } from './actionLink.styles';
import { Box, CircularProgress, Tooltip } from '@mui/material';
import { isDefined } from '@crazygames/utils/isDefined';

export const ACTION_LINK_HEIGHT = 80;

export interface ActionLinkButton {
  isLoading?: boolean;
  title: string;
  callback: () => void;
  tooltip?: string;
  color?: ButtonColor;
  icon?: JSX.Element;
}

export interface ActionLinkProps {
  primaryButton?: ActionLinkButton;
  primaryButtonDisabled?: boolean;
  secondaryButton?: ActionLinkButton;
  secondaryButtonDisabled?: boolean;
  middleButton?: ActionLinkButton;
  middleButtonDisabled?: boolean;
  containerRef?: React.RefObject<HTMLDivElement>;
  showOnlyPrimaryButton?: boolean;
}

const ActionLink: React.FC<ActionLinkProps> = ({
  primaryButton,
  primaryButtonDisabled = false,
  middleButton,
  middleButtonDisabled = false,
  secondaryButton,
  secondaryButtonDisabled = false,
  showOnlyPrimaryButton = false,
}) => {
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerLeft, setContainerLeft] = useState(0);

  useEffect(() => {
    const updateDimensions = () => {
      const contentContainer = document.querySelector('.app--content');

      if (contentContainer) {
        const rect = contentContainer.getBoundingClientRect();
        setContainerWidth(rect.width);
        setContainerLeft(rect.left);
      } else {
        const sidebarElement = document.querySelector('.MuiDrawer-paper');
        const sidebarWidth = sidebarElement ? sidebarElement.getBoundingClientRect().width : 240;
        setContainerWidth(window.innerWidth - sidebarWidth);
        setContainerLeft(sidebarWidth);
      }
    };

    updateDimensions();

    window.addEventListener('resize', updateDimensions);

    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, []);

  const containerStyle = {
    width: `${containerWidth}px`,
    left: `${containerLeft}px`,
  };

  const renderPrimaryButton = () => {
    if (!isDefined(primaryButton)) {
      return <></>;
    }
    return (
      <StyledButton
        variant="contained"
        color={primaryButton.color || 'white'}
        disabled={primaryButtonDisabled || primaryButton.isLoading}
        onClick={primaryButton.callback}
        sx={{ marginLeft: 'auto' }}
      >
        {primaryButton.title}
        <Box sx={{ width: 20, ml: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {!primaryButton.isLoading && (primaryButton.icon || <ArrowRight />)}
          {primaryButton.isLoading && <CircularProgress disableShrink={true} size={20} />}
        </Box>
      </StyledButton>
    );
  };

  return (
    <ActionLinkContainer style={containerStyle}>
      {!showOnlyPrimaryButton && (
        <>
          {secondaryButton && (
            <StyledButton
              variant="contained"
              color={secondaryButton.color || 'grey'}
              disabled={secondaryButtonDisabled}
              onClick={secondaryButton.callback}
              sx={{ marginRight: 'auto' }}
            >
              <ArrowLeft />
              {secondaryButton.title}
            </StyledButton>
          )}
          {middleButton && (
            <StyledButton
              variant="contained"
              color={middleButton.color || 'grey'}
              disabled={middleButtonDisabled}
              onClick={middleButton.callback}
            >
              {middleButton.title}
            </StyledButton>
          )}
        </>
      )}
      {primaryButton?.tooltip && primaryButtonDisabled ? (
        <Tooltip title={primaryButton.tooltip} placement="left">
          {renderPrimaryButton()}
        </Tooltip>
      ) : (
        renderPrimaryButton()
      )}
    </ActionLinkContainer>
  );
};

export default ActionLink;
